import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Fab,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ResponseOfSeries } from "services/KnowfullyAdminClient";
import { Mode } from "types/common-types";
import ModulesSection from "./components/ModulesSection";

const useStyles = makeStyles((theme: Theme) => ({
  tableTitle: {
    margin: "20px 0",
  },
}));

const SeriesDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const styles = useStyles(useTheme());
  const { seriesService, seriesUrl, seriesEditUrl } = useGlobalContext();

  const seriesResult = useQuery<ResponseOfSeries>(
    ["seriesById", id],
    () => seriesService.getSeriesById(id!),
    {
      enabled: !!id,
    }
  );

  const series = seriesResult.data?.data;
  const title = series?.seriesTitle || "";
  useTitle(title);

  if (!id) return <Navigate to={seriesUrl()} replace />;
  if (seriesResult.isLoading) return <Loader />;
  if (seriesResult.isError) {
    console.log(seriesResult.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="seriesLoadError" />
        </Alert>
      </Box>
    );
  }

  const onClickUpdate = () => {
    navigate(seriesEditUrl(id));
  };

  const rows = [
    {
      title: t({ id: "menuFormNameLabel" }),
      component: series?.seriesTitle && <span>{series.seriesTitle}</span>,
    },
    {
      title: t({ id: "menuFormDescriptionLabel" }),
      component: series?.description && <span>{series.description}</span>,
    },
    {
      title: t({ id: "menuFormRequireSequentialCompletionLabel" }),
      component: (
        <Switch
          checked={series?.priorRequirementRequired}
          color="primary"
          disabled={true}
        />
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={seriesUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>

      <h1>{title}</h1>

      <Box my={3} display="flex" justifyContent="center">
        <Fab color="primary" onClick={onClickUpdate}>
          <EditIcon />
        </Fab>
      </Box>

      <Typography variant="h5" sx={styles.tableTitle}>
        <T id="seriesLabel" />
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ModulesSection series={series!} mode={Mode.View} />
    </>
  );
};

export default SeriesDetails;
